import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import axios from 'axios';
import { UserRole, User } from '../types';

/* ── Server user shape (from /api/me) ──────────────────────────────────── */
export interface ServerUser {
  id:          string;
  username:    string;
  displayName: string;
  role:        string;   // 'admin' | 'national' | 'state' | 'discom' | 'consumer' | 'vendor'
  org:         string;
  scope:       string;
}

/* ── Map server role → UserRole enum ───────────────────────────────────── */
const SERVER_ROLE_MAP: Record<string, UserRole> = {
  admin:    UserRole.NATIONAL,
  national: UserRole.NATIONAL,
  state:    UserRole.STATE,
  discom:   UserRole.DISCOM,
  consumer: UserRole.CONSUMER,
  vendor:   UserRole.VENDOR,
};

/* ── Persona users (for demo role switching) ────────────────────────────── */
const roleUsers: Record<UserRole, User> = {
  [UserRole.NATIONAL]: {
    name: 'Ananya Sharma',
    role: UserRole.NATIONAL,
    org: 'MNRE / National',
    scope: 'All India',
  },
  [UserRole.STATE]: {
    name: 'Ramesh Patel',
    role: UserRole.STATE,
    org: 'GEDA',
    scope: 'Gujarat',
  },
  [UserRole.DISCOM]: {
    name: 'Er. Mehta',
    role: UserRole.DISCOM,
    org: 'PGVCL',
    scope: 'Ahmedabad Circle',
  },
  [UserRole.CONSUMER]: {
    name: 'Amitbhai Patel',
    role: UserRole.CONSUMER,
    org: 'Plant GJ-0482',
    scope: '6 kWp · Sofar · PGVCL Ahmedabad',
  },
  [UserRole.VENDOR]: {
    name: 'Zebron Solar',
    role: UserRole.VENDOR,
    org: 'Vendor / EPC',
    scope: '342 Plants',
  },
};

export const roleHomePaths: Record<UserRole, string> = {
  [UserRole.NATIONAL]: '/national/overview',
  [UserRole.STATE]:    '/state/overview',
  [UserRole.DISCOM]:   '/discom/overview',
  [UserRole.CONSUMER]: '/consumer/dashboard',
  [UserRole.VENDOR]:   '/vendor/overview',
};

/* ── Context shape ──────────────────────────────────────────────────────── */
interface AuthContextValue {
  /* Auth state */
  isAuthenticated: boolean;
  isLoading:       boolean;
  serverUser:      ServerUser | null;   // actual logged-in user
  setAuthUser:     (u: ServerUser) => void;
  logout:          () => Promise<void>;

  /* Role / demo switching */
  currentRole:    UserRole;
  currentUser:    User;
  setRole:        (role: UserRole) => void;

  /* Admin "viewing as" */
  adminViewingAs:    UserRole | null;
  setAdminViewingAs: (r: UserRole | null) => void;
  isAdmin:           boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/* ── Provider ───────────────────────────────────────────────────────────── */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading]         = useState(true);
  const [serverUser, setServerUser]       = useState<ServerUser | null>(null);
  const [currentRole, setCurrentRole]     = useState<UserRole>(UserRole.NATIONAL);
  const [adminViewingAs, setAdminViewingAs] = useState<UserRole | null>(null);

  /* On mount: check existing session via /api/me */
  useEffect(() => {
    axios.get<ServerUser>('/api/me', { withCredentials: true })
      .then(res => {
        setServerUser(res.data);
        const mapped = SERVER_ROLE_MAP[res.data.role] ?? UserRole.NATIONAL;
        setCurrentRole(mapped);
      })
      .catch(() => {
        setServerUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const setAuthUser = useCallback((u: ServerUser) => {
    setServerUser(u);
    const mapped = SERVER_ROLE_MAP[u.role] ?? UserRole.NATIONAL;
    setCurrentRole(mapped);
    setAdminViewingAs(null);
  }, []);

  const logout = useCallback(async () => {
    await axios.post('/api/logout', {}, { withCredentials: true }).catch(() => {});
    setServerUser(null);
    setCurrentRole(UserRole.NATIONAL);
    setAdminViewingAs(null);
  }, []);

  const setRole = useCallback((role: UserRole) => {
    setCurrentRole(role);
  }, []);

  const isAuthenticated = serverUser !== null;
  const isAdmin         = serverUser?.role === 'admin';

  /* The effective role: admin may be "viewing as" another role */
  const effectiveRole = adminViewingAs ?? currentRole;

  const currentUser: User = {
    ...roleUsers[effectiveRole],
    name: isAuthenticated && !adminViewingAs
      ? (serverUser?.displayName ?? roleUsers[effectiveRole].name)
      : roleUsers[effectiveRole].name,
    org: isAuthenticated && !adminViewingAs
      ? (serverUser?.org || roleUsers[effectiveRole].org)
      : roleUsers[effectiveRole].org,
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      serverUser,
      setAuthUser,
      logout,
      currentRole: effectiveRole,
      currentUser,
      setRole,
      adminViewingAs,
      setAdminViewingAs,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
